import { useRefCallback } from "@enfusion-ui/hooks";
import { Button, TextInput } from "@enfusion-ui/web-components";
import { styled } from "@enfusion-ui/web-core";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";

export type KeyValueInputType = [string, string];

const Container = styled.div<{ noMargin: boolean }>`
  display: flex;
  width: 100%;
  margin-bottom: ${({ noMargin }) => (noMargin ? "0px" : "var(--spacing)")};
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--spacing);
`;

const BtnWrapper = styled.div`
  width: 38px;
  margin: auto 0 0;
`;

const StyledButton = styled(Button)`
  height: 38px;
  min-width: 36px;
`;

const InputWrapper = styled.div`
  flex: 1;
`;

export type ValuesInputProps = {
  defaultValues: Array<KeyValueInputType>;
  max?: number;
  onChange: (values: Array<KeyValueInputType>) => void;
  disabled?: boolean;
  clearable?: boolean;
};

export const KeyValueControl: React.FC<ValuesInputProps> = ({
  defaultValues,
  max,
  onChange,
  disabled,
  clearable,
}) => {
  const [valueState, setValueState] = React.useState<
    Array<{ key: string; value: string } | null>
  >(defaultValues.map((i) => ({ key: i[0], value: i[1] })));

  React.useEffect(() => {
    onChange([
      ...(valueState
        .filter((i) => i !== null && i.value?.length !== 0)
        .map((i) => [i?.key, i?.value]) as unknown as Array<KeyValueInputType>),
    ]);
  }, [valueState]);

  const removeEntry = useRefCallback(
    (idx: number) => {
      return () => {
        setValueState((state) => {
          const newState = [...state];
          return newState.filter((item, index) => index != idx);
        });
      };
    },
    [valueState]
  );

  const addEntry = useRefCallback(() => {
    setValueState((state) => [...state, { key: "", value: "" }]);
  }, [valueState]);

  const changeEntryLabel = useRefCallback((idx: number) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setValueState((state) => {
        const newState = [...state];
        const row = newState[idx];
        if (row !== null) {
          newState[idx] = { ...row, key: e.target.value.toString() };
        }
        return newState;
      });
    };
  }, []);

  const changeEntryValue = useRefCallback(
    (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValueState((state) => {
        const newState = [...state];
        const row = newState[idx];
        if (row !== null) {
          newState[idx] = { ...row, value: e.target.value.toString() };
        }
        return newState;
      });
    },
    []
  );

  const nonNullCount = valueState.filter((i) => i !== null).length;
  const canAdd = typeof max === "number" ? nonNullCount < max : true;

  return (
    <>
      {valueState.map((entry, idx) => {
        if (entry === null)
          return <div key={`${idx}`} style={{ display: "none" }}></div>;
        const { key, value } = entry;
        return (
          <Container
            noMargin={!canAdd && idx === valueState.length - 1}
            key={`${idx}`}
          >
            <InputWrapper>
              <TextInput
                value={key}
                onChange={changeEntryLabel(idx)}
                label={idx === 0 ? "Key" : undefined}
                hideLabel={idx !== 0}
                disabled={disabled}
                clearable={clearable}
              />
            </InputWrapper>
            <InputWrapper>
              <TextInput
                value={value}
                onChange={changeEntryValue(idx)}
                label={idx === 0 ? "Value" : undefined}
                hideLabel={idx !== 0}
                disabled={disabled}
                clearable={clearable}
              />
            </InputWrapper>
            <BtnWrapper>
              <StyledButton name="remove" onClick={removeEntry(idx)}>
                <FontAwesomeIcon icon={faTrash} size="xs" />
              </StyledButton>
            </BtnWrapper>
          </Container>
        );
      })}
      {canAdd && <Button onClick={addEntry}>Add</Button>}
    </>
  );
};
